import React, { lazy, RefObject } from "react";
import { IntlProvider } from "react-intl";

import { CanvasPageThrobber } from "@use/react-canvas-throbber";
import {
  Route,
  RouteErrorBoundary,
  RouteNotFound,
  Router,
} from "@use/react-router";

import { getReactIntlLocale } from "@apps/mf-overview/src/ts/i18n/getReactIntlLocale";
import { loadLocale } from "@apps/mf-overview/src/ts/i18n/loadLocale";
import { LocaleProvider } from "@apps/mf-overview/src/ts/i18n/LocaleContext";

import { createStore } from "@apps/mf-overview/src/ts/stores/createStore";
import { StoreProvider } from "@apps/mf-overview/src/ts/stores/injectStore";

import { InitialFade } from "@apps/mf-overview/src/ts/components/InitialFade";

/**
 * Specification of the runtime state of <Root />.
 */
type RootState = {
  /**
   * Specifies if a route has been opened by the router so far (we're going to
   * rely on this piece of information to determine when to hide the throbber
   * that's displayed initially).
   */
  wasRouteOpened?: boolean;
};

/**
 * Root of the application render tree.
 *
 * We're going to mount the basic layout of the application, a router to manage
 * page rendering and an initial throbber which is going to be displayed until
 * the application is ready to be rendered.
 */
class RootUnwrapped extends React.PureComponent<{}, RootState> {
  /**
   * Set up initial state, so we can safely use this.state prior to the first
   * setState call!
   */
  public readonly state: RootState = {};

  /**
   * Mount the application render tree here.
   */
  public render(): React.JSX.Element {
    return (
      <>
        <Router onRouteOpened={this.onRouteOpened}>
          <Route path="/" render={this.renderDeprecationMessage} />
          <RouteNotFound render={this.renderNotFound} />
          <RouteErrorBoundary render={this.renderErrorBoundary} />
        </Router>

        {this.renderThrobber()}
      </>
    );
  }

  /**
   * Helper that'll render a throbber that's going to be displayed while we're
   * waiting for data to be ready.
   */
  protected renderThrobber(): React.JSX.Element | null {
    return (
      <InitialFade in={!this.state.wasRouteOpened}>
        {(ref) => <CanvasPageThrobber ref={ref} />}
      </InitialFade>
    );
  }

  protected renderDeprecationMessage = async () => {
    const DeprecationMessagePage = lazy(async () => ({
      default: (
        await import(
          "@apps/mf-overview/src/ts/components/pages/DeprecationMessage/_deprecation-message"
        )
      ).DeprecationMessagePage,
    }));

    return this.wrapRender((ref) => <DeprecationMessagePage ref={ref} />);
  };

  /**
   * Helper that'll render a fallback error message if the currently requested
   * url doesn't exist within the application.
   */
  protected renderNotFound = async () => {
    const PageNotFound = lazy(async () => ({
      default: (
        await import(
          "@apps/mf-overview/src/ts/components/errors/PageNotFound/PageNotFound"
        )
      ).PageNotFound,
    }));

    return this.wrapRender((ref) => <PageNotFound ref={ref} />);
  };

  /**
   * Helper that'll render a fallback error message, if an exception was
   * thrown during rendering of one of the pages within the router.
   */
  protected renderErrorBoundary = async () => {
    const { ClientError } = await import(
      "@apps/mf-overview/src/ts/components/errors/ClientError/ClientError"
    );

    return this.wrapRender((ref) => <ClientError ref={ref} />);
  };

  /**
   * Listener that's going to be triggered whenever a route has been opened
   * (ie. requested and rendered).
   *
   * Here we're going to register that a route has been successfully opened,
   * so that we can hide the throbber.
   */
  protected onRouteOpened = () => {
    if (!this.state.wasRouteOpened) {
      this.setState({ wasRouteOpened: true });
    }
  };

  /**
   * Helper that'll wrap the given render method in a promise, so that we can
   * defer rendering until required data is available.
   *
   * Further more
   * Helper that'll wrap the given render method in a promise, which is going
   * to be resolved once all basic data for the application has been in once store and locale is available for injection.
   */
  protected async wrapRender(
    render: (
      ref: RefObject<HTMLDivElement | null>,
    ) => Promise<React.JSX.Element> | React.JSX.Element,
  ): Promise<React.JSX.Element> {
    // At some point we might want to apply locale as a state variable that
    // could easily be changed, but for now it's simply hardcoded here
    const locale = "da-dk";

    // Wait for all required bundles and data to become available before
    // moving on to render the actual page
    const [{ CSS, Header }, store, localeData] = await Promise.all([
      import("@apps/mf-overview/src/ts/components/layout"),
      createStore(),
      loadLocale(locale),
    ]);

    const { formats, ...messages } = localeData;

    // Actually render the content
    return (
      <StoreProvider value={store}>
        <LocaleProvider value={localeData}>
          <IntlProvider
            locale={getReactIntlLocale(locale)}
            messages={messages as unknown as Record<string, string>}
            formats={formats}
            textComponent="span"
          >
            <>
              <CSS />
              <Header visible={this.state.wasRouteOpened} />

              <InitialFade in={this.state.wasRouteOpened}>
                {(ref) => render(ref)}
              </InitialFade>
            </>
          </IntlProvider>
        </LocaleProvider>
      </StoreProvider>
    );
  }
}

// tslint:disable-next-line variable-name
export const Root = RootUnwrapped;
